import { NgoPartnerDto } from './../../../models/trofaco/ngoPartnerDto';
import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { PlantingSiteDto } from '../../../models/trofaco/plantingSiteDto';
import { PlantingSiteDetailDto } from '../../../models/trofaco/plantingSiteDetailDto';
import { speciesDto } from '../../../models/trofaco/speciesDto';
import { PlantingSiteAreaDto } from 'src/app/models/trofaco/plantingSiteAreaDto';
import { PlantingSiteSpeciesDto } from 'src/app/models/trofaco/plantingSiteSpeciesDto';
import { CertificateDto } from 'src/app/models/trofaco/certificateDto';
import { throwError } from 'rxjs';
import { CsvDto } from 'src/app/models/trofaco/csvDto';


@Injectable({
    providedIn: 'root'
})
export class PlantingSiteService {

    constructor(private requestService: RequestService) {
    }

    async getAll() {
        let result = await this.requestService.getApi<PlantingSiteDto[]>('plantingSites');
        return result.data;
    }

    async getById(id: string): Promise<PlantingSiteDetailDto> {
        let result = await this.requestService.getApi<PlantingSiteDetailDto>('plantingSites/' + id);
        return result.data;
    }

    async save(site: PlantingSiteDetailDto): Promise<string> {
        let data: any = {
            title: site.title,
            description: site.description,
            country: site.country,
            region: site.region,
            ngoPartnerIds: site.partners.map(partner => partner.ngoPartnerId),
            communityOwner: site.landOwner,
            communityOwnerDescription: site.landOwnerDescription,
            plantingDate: site.plantingDate,
            singleDonorMessage: site.singleDonorMessage,
            totalTrees: site.totalTrees,
            lat: site.lat,
            lon: site.lon,
            photoPosition: site.photoPosition,
            code: site.code,
            pdfUrl: site.pdfUrl,
            enabledBanner: site.enabledBanner,
            bannerHeader: site.bannerHeader,
            bannerDescription: site.bannerDescription
        };
        if (site.id) {
            await this.requestService.patch('plantingSites/update-planting-site', { plantingSiteId: site.id, ...data });
            return site.id;
        } else {
            let result = await this.requestService.post('plantingSites/create-planting-site', data);
            return result.data;
        }
    }

    async addPlantingSiteArea(plantingSiteAreaFormData: FormData): Promise<PlantingSiteAreaDto> {
        let result = await this.requestService.post('plantingSites/create-planting-site-area', plantingSiteAreaFormData)
        return result.data;
    }

    async updatePlantingSiteArea(plantingSiteArea: FormData): Promise<PlantingSiteAreaDto> {
        let result = await this.requestService.patch('plantingSites/update-planting-site-area', plantingSiteArea);
        return result.data;
    }

    async deletePlantingSiteArea(platingSiteAreaId: string) {
        let result = await this.requestService.delete('plantingSites/delete-planting-site-area/' + platingSiteAreaId);
        return result.data;
    }

    updatePlantedTreesPhoto(file: File, siteId: string) {
        let formData = new FormData();
        formData.append('SpeciesPhoto', file, file.name);
        formData.append('SpeciesId', siteId);
        return this.requestService.post('plantingSites/upload-planted-trees-photo', formData);
    }

    async getSpeciesById(id: string): Promise<speciesDto> {
        let result = await this.requestService.getApi<speciesDto>('plantingSites/plating-species/' + id);
        return result.data;
    }

    async addCertificate(siteId: string, certificate: CertificateDto): Promise<CertificateDto> {
        let result = await this.requestService.post('certificates/create-planting-site-certificate', { plantingSiteId: siteId, ...certificate });
        return result.data;
    }

    async updateCertificate(certificate: CertificateDto): Promise<string> {
        await this.requestService.patch('certificates/update-planting-site-certificate', certificate);
        return certificate.id;
    }


    async deletCertificate(id: string): Promise<string> {
        let result = await this.requestService.delete('certificates/delete-planting-site-certificate/' + id);
        return result.data;
    }

    async deletDocument(id: string): Promise<string> {
        let result = await this.requestService.delete('plantingSites/delete-planting-site-document/' + id);
        return result.data;
    }

    async uploadCollectionPhoto(file: File, collectionId: string, lat: number, lon: number): Promise<string> {
        let formData = new FormData();
        formData.append('photo', file, file.name);
        formData.append('photoCollectionId', collectionId);
        formData.append('lat', lat.toString());
        formData.append('lon', lon.toString());
        let result = await this.requestService.post('photoCollection/add-photo-to-collection', formData);
        return result.data;
    }

    setCollectionThumbnail(collectionId: string, photoId: string) {
        return this.requestService.patch(`photoCollection/set-thumbnail/${collectionId}?photoId=${photoId}`, null);
    }

    updateSitePhoto(file: File, siteId: string) {
        let formData = new FormData();
        formData.append('headerPhoto', file, file.name);
        formData.append('plantingSiteId', siteId);
        return this.requestService.post('plantingSites/upload-planting-site-header-photo', formData);
    }

    updateLandOwnerPhoto(file: File, siteId: string) {
        let formData = new FormData();
        formData.append('LandOwnerPhoto', file, file.name);
        formData.append('plantingSiteId', siteId);
        return this.requestService.post('plantingSites/upload-planting-site-land-owner-photo', formData);
    }

    updateSitePDF(file: File, siteId: string) {
        let formData = new FormData();
        formData.append('pdfFile', file, file.name);
        formData.append('plantingSiteId', siteId);
        return this.requestService.post('plantingSites/upload-planting-site-pdf', formData);
    }

    deleteLandOwnerPhoto(siteId: string) {
        return this.requestService.delete('plantingSites/delete-land-owner-photo?plantingSiteId=' + siteId);
    }

    updatePlantingSiteAreaPhoto(file: File, siteId: string) {
        let formData = new FormData();
        formData.append('coverPhoto', file, file.name);
        formData.append('plantingSiteAreaId', siteId);
        return this.requestService.post('plantingSites/upload-planting-site-area-photo', formData);
    }

    async getCountries(): Promise<string[]> {
        let result = await this.requestService.getApi<string[]>('plantingSites/countries');
        return result.data;
    }

    async getRegions(): Promise<string[]> {
        let result = await this.requestService.getApi<string[]>('plantingSites/regions');
        return result.data;
    }

    async getAllSpecies(): Promise<speciesDto[]> {
        let result = await this.requestService.getApi<speciesDto[]>('plantingSites/get-all-planting-site-species');
        return result.data;
    }

    async saveSpecies(species: PlantingSiteSpeciesDto, plantingSiteId: string): Promise<string> {
        let data = {
            speciesId : species.speciesId,
            count: species.count,
            species: species.species,
            description : species.description
        };

        if (species.id) {
            await this.requestService.patch('plantingSites/update-planting-site-species', { id: species.id, ...data });
            return species.id;
        } else {
            let result = await this.requestService.post('plantingSites/create-planting-site-species', { plantingSiteId: plantingSiteId, ...data });
            return result.data;
        }
    }

    async deleteSpecies(id: string): Promise<string> {
        let result = await this.requestService.delete('plantingSites/delete-planting-site-species?id=' + id);
        return result.data;
    }

    async deletePlantingSite(id: string): Promise<string> {
        let result = await this.requestService.delete('plantingSites/delete-planting-site?plantingSiteId=' + id);
        return result.data;
    }

    async getFlightPathData(id: string): Promise<CsvDto> {
        let result = await this.requestService.getApi<CsvDto>('plantingSites/flight-path/' + id);
        return result.data;
    }

    async createSpeies(name: string): Promise<speciesDto> {
        let result = await this.requestService.post('plantingSites/create-species',{name: name});
        return result.data;
    }
}
